/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable } from '@angular/core';
import { BuilderUserContext } from '@gh/builder-shared';
import {
  ChecklistElementType, CheckUsage,
  MobileChecklistsRestService,
} from '@gh/builder-shared-data';
import { TranslationService } from '@gh/core-mls';
import {
  DictionaryService,
  formStateSnapshot,
  getRouteParam,
  InitViewService,
  isRouteForCreationMode,
} from '@gh/common-shared';
import { ID } from '@gh/common-shared-data';

import { Observable } from 'rxjs';

import {
  ChecklistDetails,
  ChecklistItem, newChecklistDetails,
  chklDetailsFromDto, toClonedChklDetails, ChklSection, ChklClause, chklDetailsToDto,
} from './checklists.model';

import { map$, switchMap$ } from '@gh/rx/operators';
import { ActivatedRouteSnapshot } from '@angular/router';

import { uniqueId } from 'lodash';
import { ComponentListCheckUsage } from '../../estimating/resources/component-list-item.model';
import { of$ } from '@gh/rx';

@Injectable()
export class ChecklistsBusinessService implements InitViewService {
  constructor(private checklistRest: MobileChecklistsRestService,
              private translations: TranslationService,
              private dictionaryService: DictionaryService,
              private userContext: BuilderUserContext) {

  }

  initView(route: ActivatedRouteSnapshot): any {
    return {
      checklist: this.findAll(false),
    };
  }

  initDetailsView(route: ActivatedRouteSnapshot): any {
    const itemId = getRouteParam(route, 'id');
    return {
      details: isRouteForCreationMode(route)
        ? newChecklistDetails(this.defineLevel()) : this.get(itemId),
      elementTypeDict: this.dictionaryService.getByEnum(ChecklistElementType),
    };

  }

  updateOrder(checkLIst: ChecklistItem[]): Observable<ChecklistItem[]> {
    return this.checklistRest.updateOrder(checkLIst);
  }
  delete(id: ID): Observable<ChecklistItem> {
    return this.checklistRest.delete(id);
  }
  undelete(id: ID): Observable<ChecklistItem> {
    return this.checklistRest.undelete(id);
  }
  checkUsage(component: ChecklistItem): Observable<CheckUsage> {
    return this.checklistRest.checkUsage(component);
  }
  deactivateOrDelete(component: ChecklistItem, checkUsage: ComponentListCheckUsage): Observable<any> {
    return this.checklistRest.deactivateOrDelete(component, checkUsage);
  }

  findAll(showDeleted: boolean): Observable<ChecklistItem[]> {
    return this.checklistRest.findAll(showDeleted);
  }

  get(id: ID): Observable<ChecklistDetails> {
    return this.checklistRest.get(id).pipe(switchMap$(
      (checklist) => checklist.parentId ?
         this.checklistRest.get(checklist.parentId)
           .pipe(map$((parentCheckList) => ({
             ...chklDetailsFromDto(checklist),
             isParentEditable: !parentCheckList.notEditable,
           }))) : of$(chklDetailsFromDto(checklist)),
    ));
  }
  clone(id: ID): Observable<ChecklistDetails> {
    return this.checklistRest.get(id).pipe(
      map$(chklDetailsFromDto),
      map$(toClonedChklDetails));
  }

  save(details: ChecklistDetails): Observable<ChecklistDetails> {
    if (details.id) {
      return this.checklistRest.update(chklDetailsToDto(details)).pipe(map$(chklDetailsFromDto));
    } else {
      return this.checklistRest.create(chklDetailsToDto(details)).pipe(map$(chklDetailsFromDto));
    }
  }

  createNewSection(): ChklSection {
    return {
      id: uniqueId('chkl-section:'),
      info: formStateSnapshot({
        title: this.translations.get('lbl_new_element'),
        elementType: undefined,
      }),
      checklistItems: [],
      isNew: true,
    };
  }
  createNewClause(): ChklClause {
    return {
      id: uniqueId('chkl-clause:'),
      title:  this.translations.get('lbl_new_item'),
    };
  }

  get officeView(): boolean {
    return this.userContext.officeView;
  }

  private defineLevel(): number {
    if (this.userContext.corporateView) {
      return 0;
    } else if (this.userContext.masterView) {
      return 1;
    } else {
      // tslint:disable-next-line:no-magic-numbers
      return 2;
    }
  }
}
